// ✅ UPDATED FULL CODE (ONLY MODALS CHANGED TO MATCH FIGMA)
// Copy-paste and replace your current component with this.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import styles from "./invoice_comment_section.module.css";
import {
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    TextArea,
    View,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { MaximizeIcon } from "../../../../../components/protected/Icons/IconsLib";
import TinyMCEEditor from "../../../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import { Button } from "react-bootstrap";

interface IInvoiceCommentSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

const InvoiceCommentSection: React.FC<IInvoiceCommentSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const [showClientNotes, setShowClientNotes] = useState<boolean>(false);

    // ✅ Keep editor content locally so parent invoice update doesn't happen per keystroke
    const [ourNotesHtml, setOurNotesHtml] = useState<string>(
        invoice?.internalDescription ?? ""
    );
    const [clientNotesHtml, setClientNotesHtml] = useState<string>(
        invoice?.description ?? ""
    );

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

    // Helper function to strip HTML tags for TextArea display
    const stripHtmlTags = useCallback((html: string | undefined) => {
        if (!html) return "";
        return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
    }, []);

    // ✅ Sync local state when invoice changes from outside (switch invoice / load data)
    useEffect(() => {
        setOurNotesHtml(invoice?.internalDescription ?? "");
    }, [invoice?.internalDescription]);

    useEffect(() => {
        setClientNotesHtml(invoice?.description ?? "");
    }, [invoice?.description]);

    const openOurNotes = useCallback(() => {
        setOurNotesHtml(invoice?.internalDescription ?? "");
        setShowMorefields(true);
    }, [invoice?.internalDescription]);

    const openClientNotes = useCallback(() => {
        setClientNotesHtml(invoice?.description ?? "");
        setShowClientNotes(true);
    }, [invoice?.description]);

    const saveOurNotes = useCallback(() => {
        onChange({ ...invoice, internalDescription: ourNotesHtml });
        setShowMorefields(false);
    }, [invoice, onChange, ourNotesHtml]);

    const cancelOurNotes = useCallback(() => {
        setOurNotesHtml(invoice?.internalDescription ?? "");
        setShowMorefields(false);
    }, [invoice?.internalDescription]);

    const saveClientNotes = useCallback(() => {
        onChange({ ...invoice, description: clientNotesHtml });
        setShowClientNotes(false);
    }, [invoice, onChange, clientNotesHtml]);

    const cancelClientNotes = useCallback(() => {
        setClientNotesHtml(invoice?.description ?? "");
        setShowClientNotes(false);
    }, [invoice?.description]);

    // ✅ Memoized plain text so it doesn't recompute on every keystroke
    const ourNotesPlain = useMemo(
        () => stripHtmlTags(ourNotesHtml),
        [ourNotesHtml, stripHtmlTags]
    );
    const clientNotesPlain = useMemo(
        () => stripHtmlTags(clientNotesHtml),
        [clientNotesHtml, stripHtmlTags]
    );

    return (
        <>
            <Flex
                direction={{ base: "column", L: "column" }}
                UNSAFE_className={styles.main_container}
            >
                <Flex direction={"row"} gap={"size-100"}>
                    {/* Notes to us */}
                    <div style={{ position: "relative", width: "50%" }}>
                        <TextArea
                            label="Notes to us"
                            width={"100%"}
                            value={ourNotesPlain}
                            onChange={(val) => {
                                setOurNotesHtml(val ?? "");
                                onChange({ ...invoice, internalDescription: val });
                            }}
                        />
                        <span
                            onClick={openOurNotes}
                            style={{
                                position: "absolute",
                                right: "8px",
                                bottom: "8px",
                                cursor: "pointer",
                            }}
                        >
                            <MaximizeIcon size={18} />
                        </span>
                    </div>

                    {/* Notes to client */}
                    <div style={{ position: "relative", width: "50%" }}>
                        <TextArea
                            label="Notes to client"
                            width={"100%"}
                            value={clientNotesPlain}
                            onChange={(val) => {
                                setClientNotesHtml(val ?? "");
                                onChange({ ...invoice, description: val });
                            }}
                        />
                        <span
                            onClick={openClientNotes}
                            style={{
                                position: "absolute",
                                right: "8px",
                                bottom: "8px",
                                cursor: "pointer",
                            }}
                        >
                            <MaximizeIcon size={18} />
                        </span>
                    </div>
                </Flex>
            </Flex>

            {/* ===================== NOTES TO US (FIGMA MODAL) ===================== */}
            {showMorefileds && (
                <DialogContainer onDismiss={cancelOurNotes} isDismissable>
                    <Dialog
                        aria-label="Notes to us"
                        isDismissable
                        onDismiss={cancelOurNotes}
                        UNSAFE_className={styles.figmaDialog}
                        width={{ base: "100%", L: "820px" }}
                    >
                        <Heading level={3} UNSAFE_className={styles.figmaTitle}>
                            Notes to us
                        </Heading>
                        <Content UNSAFE_className={styles.figmaContent}>
                            <View UNSAFE_className={styles.figmaEditorShell}>
                                <TinyMCEEditor
                                    initialValue={ourNotesHtml}
                                    onChange={(html: string) => setOurNotesHtml(html ?? "")}
                                    height={360}
                                />
                            </View>
                            <View UNSAFE_className={styles.figmaFooter}>
                                <Flex justifyContent="end" gap="size-200">
                                    <Button
                                        onClick={cancelOurNotes}
                                        className={styles.figmaCancelBtn}
                                    >
                                        {_t("cancel")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={saveOurNotes}
                                        className={styles.figmaSaveBtn}
                                    >
                                        {isEdit ? _t("update") : _t("save")}
                                    </Button>
                                </Flex>
                            </View>
                        </Content>
                    </Dialog>
                </DialogContainer>
            )}

            {/* ===================== NOTES TO CLIENT (FIGMA MODAL) ===================== */}
            {showClientNotes && (
                <DialogContainer onDismiss={cancelClientNotes} isDismissable>
                    <Dialog
                        aria-label="Notes to client"
                        isDismissable
                        onDismiss={cancelClientNotes}
                        UNSAFE_className={styles.figmaDialog}
                        width={{ base: "100%", L: "820px" }}
                    >
                        <Heading level={3} UNSAFE_className={styles.figmaTitle}>
                            Notes to client
                        </Heading>

                        <Content UNSAFE_className={styles.figmaContent}>
                            <View UNSAFE_className={styles.figmaEditorShell}>
                                <TinyMCEEditor
                                    initialValue={clientNotesHtml}
                                    onChange={(html: string) => setClientNotesHtml(html ?? "")}
                                    height={360}
                                />
                            </View>
                            <View UNSAFE_className={styles.figmaFooter}>
                                <Flex justifyContent="end" gap="size-200">
                                    <Button
                                        onClick={cancelClientNotes}
                                        className={styles.figmaCancelBtn}
                                    >
                                        {_t("cancel")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={saveClientNotes}
                                        className={styles.figmaSaveBtn}
                                    >
                                        {isEdit ? _t("update") : _t("save")}
                                    </Button>
                                </Flex>
                            </View>
                        </Content>
                    </Dialog>
                </DialogContainer>
            )}
        </>
    );
};

export default InvoiceCommentSection;
